/* eslint no-console: 0 */
import axios from 'axios'
import posthog from './posthog.js'

import browserSniffer from '../helpers/browserSniffer.js'
import {
  consoleLogsEnabled,
  isPosthogEnabled,
  posthogLoggingEnabled,
} from '../config/features.js'
import pkg from '../../../package.json'

function selectKeys(obj, keys) {
  const result = {}
  for (const k of keys) {
    result[k] = obj[k]
  }
  return result
}

export class Logger {
  /**
   * A mapping of key/value pairs to include with every log message sent from
   * this Logger instance. It is meant to be accessed and manipulated externally.
   */
  globals = {}
  /**
   * Sends the log to the aggregator
   * @param {String} level The log level (info, warn, error)
   * @param {String} message A message indicating the event context
   * @param {Object} data The payload to be sent
   */
  send(severity, message, sendData) {
    // Merge some base metadata with the custom data from the log call
    const { clientHeight, clientWidth } = document.documentElement
    const browser = browserSniffer.getBrowserInfo()

    if (browser.name === `Safari`) {
      severity = `warn`
      message = `Safari bug. `
    }

    let recordingURL = ''
    if (isPosthogEnabled()) {
      recordingURL = posthog.get_session_replay_url() || ''
    }

    const metadata = Object.assign(
      {
        browser: {
          name: browser.name,
          version: browser.version,
          viewport: {
            width: clientWidth,
            height: clientHeight,
            string: `${clientWidth} x ${clientHeight}`,
          },
        },
        hostname: location.hostname,
        pathname: location.pathname,
        recordingURL,
      },
      this.globals,
      selectKeys(sendData, ['message', 'stack']),
    )

    const data = {
      app_name: pkg.name,
      app_version: window.drayVersion || 'unknown',
      message,
      metadata,
      severity,
      timestamp: new Date(),
    }

    const headers = {}
    // Get Kinde token for authorization
    let token = null
    try {
      const raw = localStorage.getItem('kindeToken')
      if (raw) token = JSON.parse(raw)
    } catch {
      /* ignore parse errors */
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    if (process.env.LOG_INDEX === 'app-local') return
    axios({
      data,
      headers,
      method: 'post',
      url: `${process.env.DNP_API_BASEURL}/marketplace/log/${process.env.LOG_INDEX}`,
    }).catch(console.error)
  }

  // Log-level aliases, matches conventions for Bristol on services
  action(msg, data) {
    posthogLoggingEnabled() &&
      posthog.capture('log', { level: 'action', msg, data })
    consoleLogsEnabled() && console.log(msg, data)
    this.send('action', msg, data)
  }
  info(msg, data) {
    posthogLoggingEnabled() &&
      posthog.capture('log', { level: 'info', msg, data })
    consoleLogsEnabled() && console.info(msg, data)
    this.send('info', msg, data)
  }
  warn(msg, data) {
    posthogLoggingEnabled() &&
      posthog.capture('log', { level: 'warn', msg, data })
    consoleLogsEnabled() && console.warn(msg, data)
    this.send('warn', msg, data)
  }
  error(msg, data) {
    posthogLoggingEnabled() &&
      posthog.capture('log', { level: 'error', msg, data })
    consoleLogsEnabled() && console.log(msg, data)
    this.send('error', msg, data)
  }
  debug(msg, data) {
    posthogLoggingEnabled() &&
      posthog.capture('log', { level: 'debug', msg, data })
    consoleLogsEnabled() && console.debug(msg, data)
  }
}

export default new Logger()
